(() => {
  'use strict';

  if (window.cjAuCartBundleUpgradesReady) return;
  window.cjAuCartBundleUpgradesReady = true;

  const bundlePrices = {
    2: 50,
    3: 70,
    4: 99,
    6: 120,
    8: 144,
  };

  function parseJson(value, fallback) {
    if (!value) return fallback;

    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  }

  function normaliseItems(items) {
    const grouped = {};

    (Array.isArray(items) ? items : []).forEach((item) => {
      const variantId = Number.parseInt(item.variant_id || item.id, 10);
      const quantity = Number.parseInt(item.quantity, 10);
      if (!variantId || !Number.isFinite(quantity) || quantity <= 0) return;
      grouped[variantId] = (grouped[variantId] || 0) + quantity;
    });

    return Object.entries(grouped).map(([variantId, quantity]) => ({
      variant_id: Number.parseInt(variantId, 10),
      quantity,
    }));
  }

  function formatPrice(amount) {
    return Number.isInteger(amount) ? String(amount) : amount.toFixed(2).replace(/\.00$/, '');
  }

  function offerFor(quantity, isBundle, currentPrice) {
    const nextTier = { 1: 2, 2: 3, 3: 4, 4: 6, 5: 6, 6: 8, 7: 8 };
    const targetQuantity = !isBundle && bundlePrices[quantity]
      ? quantity
      : nextTier[quantity];
    const targetPrice = bundlePrices[targetQuantity];
    if (!targetPrice || !Number.isFinite(currentPrice)) return null;

    const extraQuantity = targetQuantity - quantity;
    if (extraQuantity === 0 && targetPrice >= currentPrice - 0.005) return null;
    const copy = extraQuantity === 0
      ? `Apply ${targetQuantity}-jar bundle pricing — $${formatPrice(targetPrice)} bundle price`
      : `Add ${extraQuantity} ${extraQuantity === 1 ? 'jar' : 'jars'} — $${formatPrice(targetPrice)} bundle price`;

    return { copy, extraQuantity, targetQuantity };
  }

  function getState(card) {
    const isBundle = card.dataset.isBundle === 'true';
    const lineQuantity = Number.parseInt(card.dataset.lineQuantity, 10) || 1;
    const lineFinalPrice = Number.parseInt(card.dataset.lineFinalPrice, 10) / 100;
    const perBundleItems = normaliseItems(parseJson(card.dataset.bundleItems, []));
    let items = perBundleItems;

    if (isBundle && lineQuantity > 1) {
      items = normaliseItems(perBundleItems.map((item) => ({
        ...item,
        quantity: item.quantity * lineQuantity,
      })));
    }

    if (!items.length && !isBundle) {
      items = normaliseItems([{
        variant_id: card.dataset.lineVariantId,
        quantity: lineQuantity,
      }]);
    }

    const currentQuantity = items.reduce((total, item) => total + item.quantity, 0);
    const offer = offerFor(currentQuantity, isBundle, lineFinalPrice, lineQuantity);

    return {
      bundleId: card.dataset.bundleId,
      configId: (card.dataset.configId || '12').replace(/[^\d]/g, ''),
      currentQuantity,
      errorMessage: card.dataset.error,
      extraQuantity: offer?.extraQuantity || 0,
      isBundle,
      items,
      perBundleItems,
      lineKey: card.dataset.lineKey,
      lineFinalPrice,
      lineProperties: parseJson(card.dataset.lineProperties, {}),
      lineQuantity,
      lineVariantId: Number.parseInt(card.dataset.lineVariantId, 10),
      parentVariantId: (card.dataset.parentVariantId || '45835332943919').replace(/[^\d]/g, ''),
      selectedLabel: card.dataset.selectedLabel,
      targetQuantity: offer?.targetQuantity || 0,
      offer,
    };
  }

  function refreshCard(card) {
    const state = getState(card);
    if (!state.items.length || !state.offer) {
      card.hidden = true;
      return;
    }

    card.hidden = false;
    card.dataset.currentQuantity = String(state.currentQuantity);
    card.dataset.targetQuantity = String(state.targetQuantity);
    card.dataset.extraQuantity = String(state.extraQuantity);

    const eyebrow = card.querySelector('.cj-au-cart-upsell__eyebrow');
    if (eyebrow) eyebrow.textContent = `${state.currentQuantity} ${state.currentQuantity === 1 ? 'jar' : 'jars'} in this bundle`;

    const current = card.querySelector('[data-cj-upsell-current]');
    if (current) {
      const names = new Map([...card.querySelectorAll('[data-cj-upsell-flavour]')].map((flavour) => [
        Number(flavour.dataset.variantId),
        flavour.querySelector('.cj-au-cart-upsell__flavour-name')?.childNodes[0]?.textContent.trim(),
      ]));
      current.textContent = `Current: ${state.items.map((item) =>
        `${names.get(item.variant_id) || 'Jar'} ×${item.quantity}`).join(', ')}`;
    }

    const message = card.querySelector('.cj-au-cart-upsell__message');
    if (message) message.textContent = state.offer.copy;

    const start = card.querySelector('[data-cj-upsell-action="start"]');
    if (start) {
      start.hidden = state.targetQuantity === 0;
      start.textContent = state.extraQuantity === 0 ? 'Apply bundle savings' : 'Choose your jars';
    }

    const picker = card.querySelector('[data-cj-upsell-picker]');
    if (picker && state.targetQuantity === 0) picker.hidden = true;

    const pickerTitle = card.querySelector('.cj-au-cart-upsell__picker-title');
    if (pickerTitle) {
      pickerTitle.textContent = `Choose ${state.extraQuantity} more ${state.extraQuantity === 1 ? 'jar' : 'jars'}`;
    }

    const confirm = card.querySelector('[data-cj-upsell-action="confirm"]');
    if (confirm) confirm.textContent = `Upgrade to ${state.targetQuantity} jars`;

    card.dataset.success = `Bundle upgraded to ${state.targetQuantity} jars.`;
  }

  function refreshCards(root = document) {
    root.querySelectorAll('[data-cj-au-cart-upsell]').forEach(refreshCard);
  }

  function buildBundlePayload(items, state) {
    const mamItems = normaliseItems(items);
    const totalQuantity = mamItems.reduce((total, item) => total + item.quantity, 0);
    const bundlePrice = bundlePrices[totalQuantity];

    if (!bundlePrice || totalQuantity !== state.targetQuantity) {
      throw new Error('The selected jars do not match an available bundle tier.');
    }

    const priceByVariant = new Map([...document.querySelectorAll('[data-cj-au-cart-upsell] [data-cj-upsell-flavour]')]
      .map((flavour) => [Number(flavour.dataset.variantId), Number(flavour.dataset.priceCents)]));
    const regularSubtotal = mamItems.reduce((total, item) => {
      const price = priceByVariant.get(item.variant_id);
      if (!Number.isFinite(price) || price <= 0) throw new Error('A selected jar price is unavailable.');
      return total + item.quantity * price / 100;
    }, 0);
    const discountPercentage = Math.max((1 - (bundlePrice / regularSubtotal)) * 100, 0);
    const bundleId = `${state.configId}_${Date.now().toString(36)}`;

    return {
      bundleId,
      items: mamItems.map((item) => ({
        id: item.variant_id,
        quantity: item.quantity,
        properties: {
          _essenify_mam_bundle_id: bundleId,
          _essenify_mam_original_bundle_id: state.configId,
          _essenify_mam_parent_variant_id: `gid://shopify/ProductVariant/${state.parentVariantId}`,
          _essenify_mam_discount_percentage: discountPercentage,
          _essenify_mam_per_bundle_qty: item.quantity,
          _essenify_mam_items: JSON.stringify(mamItems),
        },
      })),
    };
  }

  function rootUrl() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  async function fetchJson(url, options = {}) {
    const response = await fetch(url, options);
    const responseText = await response.text();
    const responseData = parseJson(responseText, null);

    if (!response.ok) {
      const error = new Error(responseData?.description || responseData?.message || `Request failed with status ${response.status}.`);
      error.status = response.status;
      throw error;
    }

    if (!responseData) throw new Error('Shopify returned an invalid response.');
    return responseData;
  }

  async function fetchCart() {
    return fetchJson(`${rootUrl()}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
  }

  async function preflightVariants(card, items) {
    const flavours = new Map([...card.querySelectorAll('[data-cj-upsell-flavour]')]
      .map((flavour) => [Number(flavour.dataset.variantId), flavour]));

    await Promise.all(normaliseItems(items).map(async (item) => {
      const flavour = flavours.get(item.variant_id);
      if (!flavour?.dataset.productHandle) throw new Error('This jar is no longer in the AU bundle range. Refresh your cart.');

      const product = await fetchJson(`${rootUrl()}products/${flavour.dataset.productHandle}.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      const variant = product.variants?.find((entry) => Number(entry.id) === item.variant_id);
      if (!variant?.available) {
        const name = flavour.querySelector('.cj-au-cart-upsell__flavour-name')?.textContent.trim() || 'A jar';
        throw new Error(`${name} is sold out. Choose another flavour.`);
      }
      if (!Number.isFinite(Number(variant.price)) || Number(variant.price) <= 0) {
        throw new Error('A jar price could not be checked. Refresh your cart.');
      }
      flavour.dataset.priceCents = String(variant.price);
    }));
  }

  function findOriginalLines(cart, state) {
    const cartItems = Array.isArray(cart?.items) ? cart.items : [];

    if (state.isBundle && state.bundleId) {
      return cartItems.filter((item) => item.properties?._essenify_mam_bundle_id === state.bundleId);
    }

    return cartItems.filter((item) => item.key === state.lineKey);
  }

  async function removeOriginalLines(lines) {
    const updates = {};
    lines.forEach((line) => {
      updates[line.key] = 0;
    });

    if (!Object.keys(updates).length) throw new Error('The original cart line could not be found.');

    return fetchJson(`${rootUrl()}cart/update.js?appstle=true`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
  }

  function drawerSectionIds() {
    const drawer = document.querySelector('cart-drawer');
    const ids = drawer?.getSectionsToRender?.().map((section) => section.id).filter(Boolean);
    return ids?.length ? ids : ['cart-drawer', 'cart-icon-bubble'];
  }

  async function addCartItems(items) {
    return fetchJson(`${rootUrl()}cart/add.js?ignorecheck=true&appstle=true`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items,
        sections: drawerSectionIds(),
        sections_url: window.location.pathname,
      }),
    });
  }

  async function fetchDrawerSections() {
    const sectionIds = drawerSectionIds();
    const sectionUrl = new URL(window.location.href);
    sectionUrl.hash = '';
    sectionUrl.searchParams.set('sections', sectionIds.join(','));
    const sections = await fetchJson(sectionUrl.toString(), {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    return { sections };
  }

  async function renderDrawer(responseData) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer?.renderContents) throw new Error('The cart drawer is unavailable.');

    let renderData = responseData;
    const requiredSections = drawerSectionIds();
    const hasAllSections = requiredSections.every((sectionId) => renderData?.sections?.[sectionId]);
    if (!hasAllSections) renderData = await fetchDrawerSections();

    drawer.classList.remove('is-empty');
    drawer.renderContents(renderData);
    refreshCards(drawer);
  }

  function cartContainsBundle(cart, bundleId) {
    return Boolean(cart?.items?.some((item) => item.properties?._essenify_mam_bundle_id === bundleId));
  }

  function propertiesSignature(properties) {
    return JSON.stringify(Object.entries(properties || {}).sort(([first], [second]) => first.localeCompare(second)));
  }

  function cartContainsOriginals(cart, originalLines) {
    return originalLines.every((original) => {
      const originalSignature = propertiesSignature(original.properties);
      return cart?.items?.some((item) => (
        Number(item.variant_id) === Number(original.variant_id)
        && propertiesSignature(item.properties) === originalSignature
        && Number(item.quantity) >= Number(original.quantity)
      ));
    });
  }

  function originalLinePayload(lines, state) {
    if (state.isBundle && state.items.length) {
      const hasPerBundleItems = Array.isArray(state.perBundleItems) && state.perBundleItems.length > 0;
      const mamItems = normaliseItems(hasPerBundleItems ? state.perBundleItems : state.items);
      const lineQuantity = hasPerBundleItems ? state.lineQuantity : 1;
      const sharedProperties = {
        ...state.lineProperties,
        _essenify_mam_items: JSON.stringify(mamItems),
      };

      return mamItems.map((item) => ({
        id: item.variant_id,
        quantity: item.quantity * lineQuantity,
        properties: {
          ...sharedProperties,
          _essenify_mam_per_bundle_qty: item.quantity,
        },
      }));
    }

    return lines.map((line) => ({
      id: line.variant_id,
      quantity: line.quantity,
      properties: line.properties || {},
    }));
  }

  async function restoreOriginalLines(lines, state) {
    let lastError;

    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        return await addCartItems(originalLinePayload(lines, state));
      } catch (error) {
        lastError = error;
        const cart = await fetchCart().catch(() => null);
        if (cartContainsOriginals(cart, lines)) return fetchDrawerSections();
      }
    }

    throw lastError || new Error('The original cart lines could not be restored.');
  }

  function setBusy(card, busy) {
    card.dataset.busy = String(busy);
    card.setAttribute('aria-busy', String(busy));

    card.querySelectorAll('button').forEach((button) => {
      if (busy) {
        button.disabled = true;
        return;
      }

      if (button.matches('[data-cj-upsell-action="increment"]')) {
        button.disabled = button.closest('[data-cj-upsell-flavour]')?.dataset.available !== 'true';
      } else if (!button.matches('[data-cj-upsell-action="decrement"], [data-cj-upsell-action="confirm"]')) {
        button.disabled = false;
      }
    });

    if (!busy) updatePicker(card);
  }

  function selectedTotal(card) {
    return Object.values(card.cjAuUpsellSelection || {}).reduce((total, quantity) => total + quantity, 0);
  }

  function updatePicker(card) {
    const state = getState(card);
    const selected = card.cjAuUpsellSelection || {};
    const total = selectedTotal(card);
    const busy = card.dataset.busy === 'true';

    card.querySelectorAll('[data-cj-upsell-flavour]').forEach((flavour) => {
      const variantId = flavour.dataset.variantId;
      const count = selected[variantId] || 0;
      const available = flavour.dataset.available === 'true';
      const countOutput = flavour.querySelector('[data-cj-upsell-count]');
      const decrement = flavour.querySelector('[data-cj-upsell-action="decrement"]');
      const increment = flavour.querySelector('[data-cj-upsell-action="increment"]');

      if (countOutput) countOutput.textContent = String(count);
      flavour.classList.toggle('is-selected', count > 0);
      if (decrement) decrement.disabled = busy || count <= 0;
      if (increment) increment.disabled = busy || !available || total >= state.extraQuantity;
    });

    const selection = card.querySelector('[data-cj-upsell-selection]');
    if (selection) selection.textContent = `${total} / ${state.extraQuantity} ${state.selectedLabel}`;

    const confirm = card.querySelector('[data-cj-upsell-action="confirm"]');
    if (confirm) confirm.disabled = busy || total !== state.extraQuantity;
  }

  function selectedVariantIds(card) {
    return Object.entries(card.cjAuUpsellSelection || {}).flatMap(([variantId, quantity]) => (
      Array.from({ length: quantity }, () => Number.parseInt(variantId, 10))
    ));
  }

  function setCardStatus(card, message) {
    const status = card.querySelector('[data-cj-upsell-status]');
    if (status) status.textContent = message || '';
  }

  function showCriticalCartError(card) {
    const message = card.dataset.criticalError;
    const cartErrors = document.getElementById('CartDrawer-CartErrors');
    if (cartErrors) cartErrors.textContent = message;
    document.querySelectorAll('#CartDrawer-Checkout, [name="checkout"]').forEach((checkout) => {
      checkout.disabled = true;
      checkout.setAttribute('aria-disabled', 'true');
    });
    setCardStatus(card, message);
  }

  async function performUpgrade(card, extraVariantIds) {
    if (card.dataset.busy === 'true') return;

    const state = getState(card);
    const targetItems = state.items.map((item) => ({ ...item }));
    extraVariantIds.forEach((variantId) => {
      const existingItem = targetItems.find((item) => item.variant_id === variantId);
      if (existingItem) existingItem.quantity += 1;
      else targetItems.push({ variant_id: variantId, quantity: 1 });
    });

    setBusy(card, true);
    setCardStatus(card, card.dataset.applying);

    let originalLines = [];
    let originalRemoved = false;
    let targetAdded = false;

    try {
      await preflightVariants(card, targetItems);
      const targetBundle = buildBundlePayload(targetItems, state);
      const cart = await fetchCart();
      originalLines = findOriginalLines(cart, state);
      if (!originalLines.length) throw new Error('The original cart line could not be found.');
      if (state.isBundle && String(originalLines[0].properties?._essenify_mam_original_bundle_id) !== state.configId) {
        throw new Error('This bundle changed. Refresh your cart.');
      }
      if (!state.isBundle && (
        originalLines[0].variant_id !== state.lineVariantId
        || originalLines[0].quantity !== state.lineQuantity
      )) throw new Error('This jar quantity changed. Refresh your cart.');

      await removeOriginalLines(originalLines);
      originalRemoved = true;

      let addResponse;
      try {
        addResponse = await addCartItems(targetBundle.items);
        targetAdded = true;
      } catch (addError) {
        const currentCart = await fetchCart().catch(() => null);
        if (!cartContainsBundle(currentCart, targetBundle.bundleId)) throw addError;
        targetAdded = true;
        addResponse = await fetchDrawerSections();
      }

      if (card.closest('cart-items')) {
        window.location.reload();
        return;
      }
      await renderDrawer(addResponse);

      window.setTimeout(() => {
        const liveRegion = document.getElementById('CartDrawer-LiveRegionText');
        if (liveRegion) liveRegion.textContent = card.dataset.success;
        document.querySelector('cart-drawer .drawer__close')?.focus();
      }, 0);
    } catch (error) {
      console.error('Error applying AU cart bundle upgrade:', error);

      if (targetAdded) {
        window.location.reload();
        return;
      }

      if (originalRemoved) {
        try {
          const restoreResponse = await restoreOriginalLines(originalLines, state);
          if (card.closest('cart-items')) {
            window.location.reload();
            return;
          }
          await renderDrawer(restoreResponse);
          window.setTimeout(() => {
            const restoredCard = document.querySelector('[data-cj-au-cart-upsell]');
            if (restoredCard) {
              setCardStatus(restoredCard, restoredCard.dataset.error);
              restoredCard.querySelector('[data-cj-upsell-action="start"]')?.focus();
            }
          }, 0);
        } catch (restoreError) {
          console.error('Error restoring cart after AU bundle upgrade:', restoreError);
          showCriticalCartError(card);
        }
        return;
      }

      setCardStatus(card, error.message || state.errorMessage);
      setBusy(card, false);
    }
  }

  async function handleClick(event) {
    const actionButton = event.target.closest('[data-cj-upsell-action]');
    if (!actionButton) return;

    const card = actionButton.closest('[data-cj-au-cart-upsell]');
    if (!card || card.dataset.busy === 'true') return;

    event.preventDefault();
    const action = actionButton.dataset.cjUpsellAction;
    const state = getState(card);
    const picker = card.querySelector('[data-cj-upsell-picker]');

    if (action === 'start') {
      if (state.extraQuantity === 0) {
        await performUpgrade(card, []);
        return;
      }

      card.cjAuUpsellSelection = {};
      actionButton.hidden = true;
      if (picker) picker.hidden = false;
      setCardStatus(card, '');
      updatePicker(card);
      picker?.querySelector('[data-cj-upsell-action="increment"]:not(:disabled)')?.focus();
      return;
    }

    if (action === 'cancel') {
      card.cjAuUpsellSelection = {};
      if (picker) picker.hidden = true;
      const start = card.querySelector('[data-cj-upsell-action="start"]');
      if (start) {
        start.hidden = false;
        start.focus();
      }
      setCardStatus(card, '');
      return;
    }

    if (action === 'confirm') {
      if (selectedTotal(card) !== state.extraQuantity) return;
      await performUpgrade(card, selectedVariantIds(card));
      return;
    }

    const flavour = actionButton.closest('[data-cj-upsell-flavour]');
    const variantId = flavour?.dataset.variantId;
    if (!flavour || !variantId) return;

    const selection = card.cjAuUpsellSelection || {};
    const currentQuantity = selection[variantId] || 0;

    if (action === 'increment' && selectedTotal(card) < state.extraQuantity && flavour.dataset.available === 'true') {
      selection[variantId] = currentQuantity + 1;
    } else if (action === 'decrement' && currentQuantity > 0) {
      selection[variantId] = currentQuantity - 1;
      if (selection[variantId] === 0) delete selection[variantId];
    }

    card.cjAuUpsellSelection = selection;
    updatePicker(card);
  }

  refreshCards();
  document.addEventListener('click', handleClick);
  document.addEventListener('shopify:section:load', (event) => refreshCards(event.target));

  // The AU product builder replaces drawer markup without publishing cartUpdate.
  // Refresh only when a newly inserted subtree contains an upsell card.
  [document.querySelector('cart-drawer'), document.querySelector('cart-items')]
    .filter(Boolean)
    .forEach((root) => {
      const observer = new MutationObserver((mutations) => {
        const hasNewCard = mutations.some((mutation) => [...mutation.addedNodes].some((node) =>
          node.nodeType === Node.ELEMENT_NODE
          && (node.matches?.('[data-cj-au-cart-upsell]')
            || node.querySelector?.('[data-cj-au-cart-upsell]'))));
        if (hasNewCard) window.requestAnimationFrame(() => refreshCards(root));
      });
      observer.observe(root, { childList: true, subtree: true });
    });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => window.requestAnimationFrame(() => refreshCards()));
  }
})();
